import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from 'src/app/services/data.service';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { ContentData } from '../../pages/content-creator/add-content/models/content.model';
import { processGenreGroups, GenreGroup, GenreItem, getGenreItemsByCategory } from '../../pages/content-creator/add-content/utils/genre-category.helper';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Component({
    selector: 'app-place-content-modal',
    templateUrl: './place-content-modal.component.html',
    styleUrls: ['./place-content-modal.component.scss'],
    standalone: false
})
export class PlaceContentModalComponent implements OnInit {
  @Input() map_id: string | number;
  @Input() city_id: string | number | null = null;

  userId: number;
  contentCreatorData: ContentData[] = [];
  isLoading = false;

  // Filter properties
  gameGenereList: GenreGroup[] = [];
  gameSystemList: any[] = [];
  gameTypeList: any[] = [];
  selectedGameGenreCategoryItems: GenreItem[] = [];

  selectedGenre: string = '';
  selectedGameSystem: string[] = [];
  selectedType: string = '';
  mapGenre: string = 'Loading...';

  // Sorting properties
  sortColumn: string = 'content_title';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    public activeModal: NgbActiveModal,
    private dataService: DataService,
    private currentUserService: CurrentUserService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.currentUserService.currentUser.subscribe((data) => {
      if (data) {
        this.userId = Number(data.id);
        this.initData();
      }
    
          this.cdr.detectChanges();});
  }

  initData() {
    this.isLoading = true;

    const productList$ = this.dataService.getproductlist('gamemaster');
    const mapGenre$ = this.dataService.getMapGenre(this.map_id).pipe(
      catchError(() => of({ res: null }))
    );

    forkJoin([productList$, mapGenre$]).pipe(
      switchMap(([productData, genreData]: [any, { res: string }]) => {
        // Handle Product List first to ensure gameGenereList is available for filtering
        if (productData?.products?.gamemaster) {
          const products = productData.products.gamemaster;
          this.gameSystemList = products.filter(item => item.type === 'game_system');
          this.gameTypeList = products.filter(item => item.type === 'game_type')
            .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0));

          const rawGenres = products.filter(item => item.type === 'game_genre');
          this.gameGenereList = processGenreGroups(rawGenres);
        }

        let filters: any = {};
        // Handle Map Genre
        if (genreData && genreData.res) {
          this.mapGenre = genreData.res;
          this.selectedGenre = this.mapGenre;
          filters.game_genre = this.selectedGenre;

          // Filter game systems based on map genre
          if (this.gameGenereList.length > 0) {
            const result = getGenreItemsByCategory(this.mapGenre, this.gameGenereList);
            this.selectedGameGenreCategoryItems = result.items;
          }
        } else {
          this.mapGenre = 'N/A';
        }

        return this.dataService.getContentDataByUserId(this.userId, filters);
      })
    ).subscribe({
      next: (contentData: any) => {
        this.isLoading = false;
        if (contentData?.results) {
          this.contentCreatorData = contentData.results;
          this.applySort();
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error initializing modal data:', err);
      
          this.cdr.detectChanges();}
    });
  }

  onFilterChange() {
    const filters = {
      game_genre: this.selectedGenre,
      gamesystem: (this.selectedGameSystem && this.selectedGameSystem.length > 0) ? this.selectedGameSystem.join(',') + ',' : '',
      type: this.selectedType
    };

    this.isLoading = true;
    this.dataService.getContentDataByUserId(this.userId, filters).subscribe({
      next: (data: any) => {
        this.isLoading = false;
        if (data?.results) {
          this.contentCreatorData = data.results;
          this.applySort();
        }
      
          this.cdr.detectChanges();},
      error: (err) => {
        this.isLoading = false;
        console.error('Error fetching filtered content:', err);
      
          this.cdr.detectChanges();}
    });
  }

  sort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  private applySort() {
    if (!this.sortColumn || this.contentCreatorData.length === 0) return;

    this.contentCreatorData.sort((a, b) => {
      const valA = a[this.sortColumn];
      const valB = b[this.sortColumn];

      if (valA === valB) return 0;
      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;

      let comparison = 0;
      if (typeof valA === 'string' && typeof valB === 'string') {
        comparison = valA.localeCompare(valB);
      } else {
        comparison = valA > valB ? 1 : -1;
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  selectContent(content: ContentData) {
    this.activeModal.close(content);
  }
}
